import { DOCUMENT } from '@angular/common';
import { DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationStart, Router } from '@angular/router';
import { filter } from 'rxjs';

import { LangService } from '@reforgium/presentia';

import { AccessibilityService } from './accessibility.service';
import { readVisibleText } from './readable-text';

type SpeechState = 'idle' | 'speaking' | 'empty' | 'error';

const CHUNK_LENGTH = 220;

@Injectable({ providedIn: 'root' })
export class AccessibilitySpeechService {
  private readonly document = inject(DOCUMENT);
  private readonly accessibility = inject(AccessibilityService);
  private readonly language = inject(LangService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  readonly supported =
    Boolean(this.document.defaultView?.speechSynthesis) && typeof SpeechSynthesisUtterance !== 'undefined';
  readonly state = signal<SpeechState>('idle');
  readonly hasPlayed = signal(false);

  private generation = 0;
  private chunks: string[] = [];
  private nextChunk = 0;
  private lastText = '';
  private lastLanguage = '';

  constructor() {
    effect(() => {
      if (!this.accessibility.settings().soundAssistant) this.stop(true);
    });

    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.stop(true));
  }

  play(): void {
    if (this.state() === 'speaking') return;
    this.start(this.pageText());
  }

  playText(text: string, language?: string): void {
    this.start(text, language);
  }

  repeat(): void {
    if (!this.hasPlayed()) return;
    this.start(this.lastText, this.lastLanguage);
  }

  stop(clearReplay = false): void {
    const wasSpeaking = this.state() === 'speaking';

    this.generation++;
    this.chunks = [];
    this.nextChunk = 0;
    this.state.set('idle');

    if (clearReplay) {
      this.hasPlayed.set(false);
      this.lastText = '';
      this.lastLanguage = '';
    }

    if (wasSpeaking) this.document.defaultView?.speechSynthesis?.cancel();
  }

  private start(text: string, language?: string): void {
    if (!this.supported || !this.accessibility.settings().soundAssistant) return;

    const normalized = text.replace(/\s+/g, ' ').trim();

    this.stop();

    if (!normalized) {
      this.state.set('empty');

      return;
    }

    this.lastText = normalized;
    this.lastLanguage = language?.toLowerCase().startsWith('ky')
      ? 'ky-KG'
      : language?.toLowerCase().startsWith('ru')
        ? 'ru-RU'
        : this.language.currentLang() === 'ky'
          ? 'ky-KG'
          : 'ru-RU';
    this.chunks = this.splitText(normalized);
    this.hasPlayed.set(true);
    this.state.set('speaking');
    this.speakNext(this.generation);
  }

  private speakNext(generation: number): void {
    if (generation !== this.generation) return;

    if (this.nextChunk >= this.chunks.length) {
      this.state.set('idle');

      return;
    }

    const utterance = new SpeechSynthesisUtterance(this.chunks[this.nextChunk++]);

    utterance.lang = this.lastLanguage;
    utterance.onend = () => this.speakNext(generation);

    utterance.onerror = () => {
      if (generation !== this.generation) return;
      this.generation++;
      this.chunks = [];
      this.state.set('error');
    };

    try {
      this.document.defaultView?.speechSynthesis.speak(utterance);
    } catch {
      this.generation++;
      this.chunks = [];
      this.state.set('error');
    }
  }

  private pageText(): string {
    const main = this.document.querySelector('main, [role="main"]');

    return main ? readVisibleText(main, 'nav, form, [role="navigation"], [role="button"]') : '';
  }

  private splitText(text: string): string[] {
    const chunks: string[] = [];
    let chunk = '';

    for (const token of text.split(/\s+/)) {
      let word = token;

      while (word.length > CHUNK_LENGTH) {
        if (chunk) chunks.push(chunk);
        chunks.push(word.slice(0, CHUNK_LENGTH));
        chunk = '';
        word = word.slice(CHUNK_LENGTH);
      }

      if (!word) continue;

      if (chunk && chunk.length + word.length + 1 > CHUNK_LENGTH) {
        chunks.push(chunk);
        chunk = '';
      }

      chunk = chunk ? `${chunk} ${word}` : word;
    }

    if (chunk) chunks.push(chunk);

    return chunks;
  }
}
